import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    List,
    Stack,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import React, { useMemo, useState } from "react";
import { ALL_ITEMS_PAGE_SIZE, PREVIEW_LIMIT, matchesSearch } from "./commerceRelations";

const PRIMARY_COLOR = "#161e55";

const primaryButtonSx = {
    borderRadius: "20px",
    backgroundColor: PRIMARY_COLOR,
    textTransform: "none",
    "&:hover": { backgroundColor: "#A8C8AF" },
};

function ViewAllDialog({ open, onClose, title, items, renderItem, getSearchFields, searchPlaceholder }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);

    const filteredItems = useMemo(
        () => items.filter((item) => matchesSearch(getSearchFields(item), searchTerm)),
        [items, getSearchFields, searchTerm]
    );
    const pageItems = filteredItems.slice(page * ALL_ITEMS_PAGE_SIZE, (page + 1) * ALL_ITEMS_PAGE_SIZE);

    const handleChangeSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleClose = () => {
        setSearchTerm("");
        setPage(0);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" aria-labelledby="view-all-title">
            <DialogTitle id="view-all-title">
                {title} ({items.length})
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    fullWidth
                    size="small"
                    label="Buscar"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={handleChangeSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                {pageItems.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                        No hay resultados para "{searchTerm}"
                    </Typography>
                ) : (
                    <List disablePadding>{pageItems.map(renderItem)}</List>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={ALL_ITEMS_PAGE_SIZE}
                    rowsPerPageOptions={[ALL_ITEMS_PAGE_SIZE]}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
                <Button onClick={handleClose} sx={{ color: PRIMARY_COLOR, textTransform: "none" }}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function RelationPanel({
    description,
    addLabel,
    onAdd,
    addDisabledReason,
    items,
    renderItem,
    emptyMessage,
    viewAllLabel,
    viewAllTitle,
    getSearchFields,
    searchPlaceholder,
    notice,
}) {
    const [isViewAllOpen, setIsViewAllOpen] = useState(false);
    const previewItems = items.slice(0, PREVIEW_LIMIT);

    const addButton = (
        <span>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAdd}
                disabled={Boolean(addDisabledReason)}
                sx={primaryButtonSx}
            >
                {addLabel}
            </Button>
        </span>
    );

    return (
        <Box>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
                <Typography variant="body2" color="text.secondary">
                    {description}
                    {items.length > PREVIEW_LIMIT && ` Mostrando los ${PREVIEW_LIMIT} más recientes.`}
                </Typography>
                {addDisabledReason ? <Tooltip title={addDisabledReason}>{addButton}</Tooltip> : addButton}
            </Stack>

            {notice && <Box mt={2}>{notice}</Box>}

            {items.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                    {emptyMessage}
                </Alert>
            ) : (
                <List disablePadding>{previewItems.map(renderItem)}</List>
            )}

            {items.length > PREVIEW_LIMIT && (
                <Box textAlign="center" mt={1}>
                    <Button onClick={() => setIsViewAllOpen(true)} sx={{ color: PRIMARY_COLOR, textTransform: "none", fontWeight: 600 }}>
                        {viewAllLabel} ({items.length})
                    </Button>
                </Box>
            )}

            <ViewAllDialog
                open={isViewAllOpen}
                onClose={() => setIsViewAllOpen(false)}
                title={viewAllTitle}
                items={items}
                renderItem={renderItem}
                getSearchFields={getSearchFields}
                searchPlaceholder={searchPlaceholder}
            />
        </Box>
    );
}

export { primaryButtonSx, PRIMARY_COLOR };
